import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider } from '@nohara-dxdev/react-theme';
import { OidcConfig, OidcProvider, ScopeProvider } from '@nohara-dxdev/react-oidc';
import { CoreProvider, I18nConfig, LoadingScreen, queryConfig } from '@nohara-dxdev/react-core';

import enLocale from '@/locales/en.json';
import jaLocale from '@/locales/ja.json';
import { MainErrorFallback } from '@/components/errors/main';
import { env } from '@/config/env';

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      })
  );

  const [oidcConfig] = React.useState<OidcConfig>(() => ({
    apiEndpoint: env.HOME_API_ENDPOINT,
    oauthEndpoint: env.OAUTH_DOMAIN,
    userPoolId: env.USER_POOL_ID,
    userPoolWebClientId: env.USER_POOL_WEB_CLIENT_ID,
    oidcCallbackPath: env.OIDC_CALLBACK_PATH,
    signOutPath: env.SIGN_OUT_PATH,
  }));

  const i18nConfig = React.useMemo(
    (): I18nConfig => ({
      resources: {
        en: {
          translation: enLocale,
        },
        ja: {
          translation: jaLocale,
        },
      },
      defaultNS: 'translation',
      fallbackNS: 'translation',
      fallbackLng: 'en',
      lng: 'en',
    }),
    []
  );

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <CoreProvider i18nConfig={i18nConfig}>
        <ErrorBoundary FallbackComponent={MainErrorFallback}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && <ReactQueryDevtools />}
            <ThemeProvider>
              <OidcProvider config={oidcConfig}>
                <ScopeProvider>{children}</ScopeProvider>
              </OidcProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </CoreProvider>
    </React.Suspense>
  );
};
